<template>
  <div class="budgets-page">
    <nav class="page-breadcrumb mb-1">
      <feather-icon icon="HomeIcon" size="14" />
      <span class="ml-25">Operações</span>
      <feather-icon icon="ChevronRightIcon" size="14" class="mx-25" />
      <span class="font-weight-bold">Budget (Orçamento por Supervisor)</span>
    </nav>

    <b-card class="mb-1">
      <b-row class="align-items-end">
        <b-col md="4" sm="12">
          <label class="small text-muted">Supervisor</label>
          <v-select
            v-model="filters.supervisorid"
            :options="supervisores"
            :reduce="s => s.supervisorid"
            label="supervisornome"
            placeholder="Todos"
            class="budget-vselect-sm"
            @input="buscar"
          >
            <template #no-options>Nenhum supervisor encontrado</template>
          </v-select>
        </b-col>
        <b-col md="2" sm="6">
          <label class="small text-muted">Categoria</label>
          <b-form-select v-model="filters.categoria" size="sm" @change="buscar">
            <b-form-select-option :value="null">Todas</b-form-select-option>
            <b-form-select-option value="ELT">ELT</b-form-select-option>
            <b-form-select-option value="GL">GL</b-form-select-option>
          </b-form-select>
        </b-col>
        <b-col md="3" sm="6">
          <label class="small text-muted">Situação</label>
          <b-form-select v-model="filters.vigentes" size="sm" @change="buscar">
            <b-form-select-option :value="null">Todos</b-form-select-option>
            <b-form-select-option value="1">Somente vigentes</b-form-select-option>
          </b-form-select>
        </b-col>
        <b-col md="3" sm="12" class="d-flex align-items-end" style="gap:6px">
          <b-button size="sm" variant="outline-secondary" @click="limparFiltros">
            <feather-icon icon="XIcon" size="13" class="mr-25" />Limpar
          </b-button>
          <b-button size="sm" variant="relief-success" class="ml-auto" @click="abrirNovo">
            <feather-icon icon="PlusIcon" size="13" class="mr-25" />Novo Budget
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <b-card>
      <div v-if="isLoading" class="text-center py-4"><b-spinner /></div>
      <div v-else-if="!budgets.length" class="text-center py-4 text-muted">
        Nenhum budget encontrado.
      </div>
      <b-table
        v-else
        :items="budgets"
        :fields="fields"
        responsive
        hover
        small
        class="mb-0"
      >
        <template #cell(supervisor_nome)="data">
          <span class="font-weight-bold">{{ data.item.supervisor_nome || data.item.supervisorid }}</span>
        </template>
        <template #cell(categoria)="data">
          <b-badge :variant="data.item.categoria === 'ELT' ? 'light-primary' : 'light-info'">
            {{ data.item.categoria || 'ELT' }}
          </b-badge>
        </template>
        <template #cell(periodo)="data">
          {{ formatarData(data.item.datainicio) }} — {{ formatarData(data.item.datatermino) }}
        </template>
        <template #cell(valor)="data">
          {{ formatarMoeda(data.item.valor) }}
        </template>
        <template #cell(valorgasto)="data">
          {{ formatarMoeda(data.item.valorgasto) }}
        </template>
        <template #cell(saldo)="data">
          <span :class="Number(data.item.saldo) <= 0 ? 'text-danger font-weight-bold' : 'text-success'">
            {{ formatarMoeda(data.item.saldo) }}
          </span>
        </template>
        <template #cell(ativo)="data">
          <b-badge :variant="data.item.ativo ? 'light-success' : 'light-secondary'">
            {{ data.item.ativo ? 'Ativo' : 'Inativo' }}
          </b-badge>
        </template>
        <template #cell(acoes)="data">
          <b-button size="sm" variant="flat-primary" class="btn-icon" @click="verDetalhes(data.item)">
            <feather-icon icon="EyeIcon" size="14" />
          </b-button>
          <b-button size="sm" variant="flat-secondary" class="btn-icon" @click="editar(data.item)">
            <feather-icon icon="EditIcon" size="14" />
          </b-button>
          <b-button size="sm" variant="flat-danger" class="btn-icon" @click="confirmarExclusao(data.item)">
            <feather-icon icon="Trash2Icon" size="14" />
          </b-button>
        </template>
      </b-table>
    </b-card>

    <budget-modal ref="budgetModal" @salvo="buscar" />
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormSelect, BFormSelectOption, BButton, BSpinner, BTable, BBadge,
} from 'bootstrap-vue';
import vSelect from 'vue-select';
import { mapActions, mapState } from 'vuex';
import Swal from 'sweetalert2';
import { useToast } from 'vue-toastification/composition';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import BudgetModal from './modal/BudgetModal.vue';

export default {
  name: 'BudgetsPage',
  components: {
    BCard, BRow, BCol, BFormSelect, BFormSelectOption, BButton, BSpinner, BTable, BBadge,
    vSelect,
    BudgetModal,
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      filters: { supervisorid: null, vigentes: null, categoria: null },
      fields: [
        { key: 'supervisor_nome', label: 'Supervisor' },
        { key: 'categoria', label: 'Categoria', class: 'text-center' },
        { key: 'periodo', label: 'Período' },
        { key: 'valor', label: 'Valor', class: 'text-right' },
        { key: 'valorgasto', label: 'Gasto', class: 'text-right' },
        { key: 'saldo', label: 'Saldo', class: 'text-right' },
        { key: 'ativo', label: 'Situação', class: 'text-center' },
        { key: 'acoes', label: 'Ações', class: 'text-center' },
      ],
    };
  },
  computed: {
    ...mapState('Budget', ['budgets', 'supervisores', 'isLoading']),
  },
  async mounted() {
    await this.carregarSupervisores();
    await this.buscar();
  },
  methods: {
    ...mapActions('Budget', ['listar', 'carregarSupervisores', 'excluir', 'setFilters']),
    async buscar() {
      this.setFilters({ ...this.filters });
      await this.listar();
    },
    limparFiltros() {
      this.filters = { supervisorid: null, vigentes: null, categoria: null };
      this.buscar();
    },
    abrirNovo() {
      this.$refs.budgetModal.abrir();
    },
    editar(item) {
      this.$refs.budgetModal.abrir(item.budgetid);
    },
    verDetalhes(item) {
      this.$router.push({ name: 'budget-detalhes', params: { id: item.budgetid } });
    },
    async confirmarExclusao(item) {
      const r = await Swal.fire({
        title: 'Excluir budget?',
        text: `Supervisor: ${item.supervisor_nome || item.supervisorid}`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sim, excluir',
        cancelButtonText: 'Cancelar',
        customClass: { confirmButton: 'btn btn-danger', cancelButton: 'btn btn-outline-secondary ml-1' },
        buttonsStyling: false,
      });
      if (!r.isConfirmed) return;
      try {
        await this.excluir(item.budgetid);
        this.toast({
          component: ToastificationContent,
          props: { title: 'Budget excluído', icon: 'CheckCircleIcon', variant: 'success' },
        });
      } catch (e) {
        // erro já exibido pelo client
      }
    },
    formatarMoeda(v) {
      const n = Number(v || 0);
      return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    },
    formatarData(d) {
      if (!d) return '-';
      const dt = new Date(d);
      if (Number.isNaN(dt.getTime())) return d;
      return dt.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
    },
  },
};
</script>

<style scoped>
.page-breadcrumb {
  font-size: 0.85rem;
  color: #6e6b7b;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

.budget-vselect-sm .vs__dropdown-toggle {
  min-height: 31px;
}
</style>
